import { api } from './api';
import { useAuthStore } from '@/stores/authStore';
import type { LoginCredentials, SignUpData, User, AuthTokens } from '@/types';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          renderButton: (el: HTMLElement, config: Record<string, unknown>) => void;
          prompt: () => void;
        };
      };
    };
    AppleID?: {
      auth: {
        init: (config: Record<string, unknown>) => void;
        signIn: () => Promise<{ authorization: { id_token: string; code: string } }>;
      };
    };
  }
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';
const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID ?? '';
const APPLE_REDIRECT_URI = import.meta.env.VITE_APPLE_REDIRECT_URI ?? '';

export const authService = {
  // ── Email Auth ────────────────────────────────────────────────

  async login(credentials: LoginCredentials): Promise<{ user: User; tokens: AuthTokens }> {
    const { data } = await api.post('/auth/login', credentials);
    return data;
  },

  async signUp(signUpData: SignUpData): Promise<{ user: User; tokens: AuthTokens }> {
    const { data } = await api.post('/auth/register', signUpData);
    return data;
  },

  async forgotPassword(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email });
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await api.post('/auth/reset-password', { token, password });
  },

  // ── Google Sign-In (GIS) ──────────────────────────────────────

  initializeGoogleSignIn(
    buttonElement: HTMLElement | null,
    onSuccess: (user: User, tokens: AuthTokens) => void,
    onError: (error: string) => void,
  ) {
    if (!window.google || !GOOGLE_CLIENT_ID) {
      console.warn('Google Sign-In SDK not loaded or client ID missing');
      return;
    }

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response: { credential: string }) => {
        try {
          const { data } = await api.post('/auth/google', {
            idToken: response.credential,
          });
          onSuccess(data.user, data.tokens);
        } catch {
          onError('Google sign-in failed. Please try again.');
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    if (buttonElement) {
      window.google.accounts.id.renderButton(buttonElement, {
        theme: 'outline',
        size: 'large',
        width: '100%',
        shape: 'pill',
        text: 'continue_with',
      });
    }
  },

  // ── Apple Sign-In ─────────────────────────────────────────────

  async initializeAppleSignIn(): Promise<void> {
    if (!window.AppleID || !APPLE_CLIENT_ID) {
      console.warn('Apple Sign-In SDK not loaded or client ID missing');
      return;
    }

    window.AppleID.auth.init({
      clientId: APPLE_CLIENT_ID,
      scope: 'name email',
      redirectURI: APPLE_REDIRECT_URI,
      usePopup: true,
    });
  },

  async signInWithApple(): Promise<{ user: User; tokens: AuthTokens }> {
    if (!window.AppleID) {
      throw new Error('Apple Sign-In not available');
    }

    const appleResponse = await window.AppleID.auth.signIn();
    const { data } = await api.post('/auth/apple', {
      idToken: appleResponse.authorization.id_token,
      authorizationCode: appleResponse.authorization.code,
    });
    return data;
  },

  // ── Token Management ──────────────────────────────────────────

  async refreshTokens(): Promise<AuthTokens> {
    const { tokens } = useAuthStore.getState();
    if (!tokens?.refreshToken) throw new Error('No refresh token');

    const { data } = await api.post('/auth/refresh', {
      refreshToken: tokens.refreshToken,
    });
    return data;
  },

  async getMe(): Promise<User> {
    const { data } = await api.get('/auth/me');
    return data;
  },

  async updateProfile(updates: Partial<User>): Promise<User> {
    const { data } = await api.patch('/auth/me', updates);
    return data;
  },

  async deleteAccount(): Promise<void> {
    await api.delete('/auth/me');
  },

  loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Sign-In'));
      document.head.appendChild(script);
    });
  },

  loadAppleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.AppleID) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src =
        'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error('Failed to load Apple Sign-In'));
      document.head.appendChild(script);
    });
  },
};
